import React, { useEffect, useState } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ListingCard from "@/components/listing/ListingCard";
import SEO from "@/components/SEO";
import { ArrowRight, Check, ShieldCheck, Sparkles, HelpCircle, ShoppingBag } from "lucide-react";
import { CATEGORY_PAGES } from "@/data/categoryContent";

const CategoryLanding = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+/, "").split("/")[0];
  const content = CATEGORY_PAGES[slug];
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    if (!content) return;
    let cancelled = false;
    try {
      const queryStr = content.filterPath.split("?")[1] || "";
      const params = new URLSearchParams(queryStr);
      const apiParams = { limit: 6 };
      for (const [k, v] of params.entries()) apiParams[k] = v;
      api.get("/listings", { params: apiParams }).then((r) => {
        if (!cancelled) setFeatured(r.data || []);
      }).catch(() => {});
    } catch {
      /* ignore */
    }
    return () => { cancelled = true; };
  }, [slug, content]);

  if (!content) return <Navigate to="/" replace />;

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": content.faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://afrimarket.org/" },
      { "@type": "ListItem", "position": 2, "name": content.h1, "item": `https://afrimarket.org/${slug}` },
    ],
  };

  const collectionJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": content.h1,
    "description": content.metaDesc,
    "url": `https://afrimarket.org/${slug}`,
    "primaryImageOfPage": content.image,
  };

  return (
    <div className="heritage-canvas">
      <SEO
        title={content.metaTitle}
        description={content.metaDesc}
        path={`/${slug}`}
        image={content.image}
        jsonLd={[collectionJsonLd, breadcrumbJsonLd, faqJsonLd]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-black/5">
        <div className="absolute inset-0 ankara-stripe opacity-50" aria-hidden />
        <div className="container-narrow relative pt-16 pb-16 lg:pt-20 lg:pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="label-overline text-[#C05A3C] mb-4">{content.heroEyebrow}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {content.h1}
            </h1>
            <p className="mt-5 font-display text-xl text-[#2A3D36] italic">{content.heroSubtitle}</p>
            <div className="mt-7 flex flex-wrap gap-3" data-testid="category-hero-ctas">
              <Link to={content.filterPath} data-testid="cta-browse-category">
                <Button className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md h-12 px-6 font-semibold">
                  <ShoppingBag className="w-4 h-4 mr-2" /> Browse listings
                </Button>
              </Link>
              <Link to="/register" data-testid="cta-sell-category">
                <Button variant="outline" className="border-[#1A2024] text-[#1A2024] hover:bg-[#1A2024] hover:text-white rounded-md h-12 px-6 font-semibold">
                  Become a Seller
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-3 text-sm text-muted-foreground">
              <ShieldCheck className="w-4 h-4 text-[#4A5D23]" />
              <span>Verified sellers · Buyer Guarantee · Worldwide shipping</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-md">
              <img src={content.image} alt={content.h1} className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="container-narrow py-16 max-w-3xl">
        <div className="space-y-5 text-lg leading-relaxed text-[#1A1A1A] font-sans-ui">
          {content.intro.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </section>

      {/* Featured live listings */}
      {featured.length > 0 && (
        <section className="container-narrow pb-16">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="label-overline text-[#C05A3C] mb-2">Available now</div>
              <h2 className="font-display text-3xl tracking-tight">Featured {content.heroEyebrow.toLowerCase()}</h2>
            </div>
            <Link to={content.filterPath} className="text-sm font-medium hover:text-[#C05A3C] inline-flex items-center gap-1" data-testid="see-all-category">
              See all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-grid">
            {featured.map((l) => <ListingCard key={l.listing_id} listing={l} />)}
          </div>
        </section>
      )}

      {/* Long-form sections */}
      <section className="container-narrow pb-16 max-w-3xl space-y-12">
        {content.sections.map((s, i) => (
          <article key={i} className="space-y-3" data-testid={`section-${i}`}>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight">{s.h2}</h2>
            <p className="text-base leading-relaxed text-[#1A1A1A] font-sans-ui">{s.body}</p>
          </article>
        ))}
      </section>

      {/* Buying tips */}
      <section className="bg-[#FBF6EE] border-y border-black/5">
        <div className="container-narrow py-14 max-w-3xl">
          <div className="label-overline text-[#C05A3C] mb-2">Smart shopping</div>
          <h2 className="font-display text-3xl mb-6">Tips for buying with confidence</h2>
          <ul className="space-y-3" data-testid="tips-list">
            {content.buyingTips.map((tip, i) => (
              <li key={i} className="flex items-start gap-3 bg-white rounded-lg p-4 border border-black/5">
                <div className="w-6 h-6 rounded-full bg-[#4A5D23] text-white flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3.5 h-3.5" />
                </div>
                <span className="text-sm leading-relaxed">{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Internal links — also explore */}
      <section className="container-narrow py-14">
        <div className="label-overline text-[#C05A3C] mb-2">Keep exploring</div>
        <h2 className="font-display text-3xl mb-6">More from AfriMarket</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="internal-links">
          {content.internalLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="group flex items-center justify-between bg-white border border-black/10 rounded-lg p-4 hover:border-[#C05A3C] hover:shadow-sm transition-all"
              data-testid={`internal-link-${link.to.replace(/\//g, "")}`}
            >
              <span className="font-medium">{link.label}</span>
              <ArrowRight className="w-4 h-4 text-[#C05A3C] group-hover:translate-x-1 transition-transform" />
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-t border-black/5">
        <div className="container-narrow py-14 max-w-3xl">
          <div className="text-center mb-10">
            <div className="label-overline text-[#C05A3C] mb-2 flex items-center justify-center gap-2">
              <HelpCircle className="w-3.5 h-3.5" /> FAQ
            </div>
            <h2 className="font-display text-3xl tracking-tight">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {content.faqs.map((item, idx) => (
              <details
                key={item.q}
                data-testid={`faq-${idx}`}
                className="group bg-[#FBF6EE] rounded-2xl border border-black/10 p-6 hover:border-[#C05A3C] transition-colors"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between font-display text-lg font-semibold gap-4">
                  <span>{item.q}</span>
                  <ArrowRight className="w-4 h-4 text-[#C05A3C] transition-transform group-open:rotate-90 shrink-0" />
                </summary>
                <p className="mt-4 text-muted-foreground leading-relaxed font-sans-ui">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="container-narrow pb-20 pt-14">
        <Card className="bg-[#C05A3C] text-white rounded-2xl border-0 p-10 lg:p-14 overflow-hidden relative">
          <div className="absolute inset-0 ankara-stripe opacity-30" aria-hidden />
          <div className="relative grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="label-overline text-white/80 mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Ready to shop?
              </div>
              <h2 className="font-display text-3xl sm:text-4xl leading-tight">
                Discover authentic {content.heroEyebrow.toLowerCase()} from trusted sellers — backed by our buyer guarantee.
              </h2>
            </div>
            <div className="md:text-right space-y-3">
              <Link to={content.filterPath} data-testid="final-cta-browse">
                <Button className="bg-white text-[#C05A3C] hover:bg-white/90 rounded-md h-12 px-8 font-semibold mr-3">
                  Browse listings
                </Button>
              </Link>
              <Link to="/register" data-testid="final-cta-sell">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#C05A3C] rounded-md h-12 px-8 font-semibold">
                  Become a Seller
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
};

export default CategoryLanding;
